"use client";

import { SafeHydrate } from "@/SafeHydrate";
import React, { useEffect, useRef, useState } from "react";
import { Leaf, ShieldCheck, Heart, Trophy, Users, ArrowRight, Quote } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from "@/components/ui/carousel";
type UseRevealOnScrollOptions = {
  once?: boolean;
  threshold?: number | number[];
  rootMargin?: string;
};
function useRevealOnScroll<T extends HTMLElement = HTMLElement>({
  once = true,
  threshold = 0.2,
  rootMargin = "0px"
}: UseRevealOnScrollOptions = {}) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) {
      return;
    }
    if (typeof window === "undefined" || typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        if (once) {
          observer.unobserve(entry.target);
        }
      } else if (!once) {
        setIsVisible(false);
      }
    }, {
      threshold,
      rootMargin
    });
    observer.observe(node);
    return () => {
      observer.disconnect();
    };
  }, [once, rootMargin, threshold]);
  return {
    ref,
    isVisible
  };
}
const features = [{
  title: "Compromiso Comunitario",
  description: "Impulsamos el desarrollo local a través de programas de infraestructura y apoyo directo a las comunidades donde operamos.",
  icon: Users
}, {
  title: "Voluntariado Corporativo",
  description: "Nuestros colaboradores dedican miles de horas anuales a proyectos sociales, desde reforestación hasta mentorías técnicas.",
  icon: Heart
}, {
  title: "Educación y Capacitación",
  description: "Invertimos en el futuro mediante becas y programas de formación técnica para jóvenes en el sector industrial.",
  icon: Trophy
}, {
  title: "Sostenibilidad Ambiental",
  description: "Implementamos procesos de economía circular para reducir nuestra huella y proteger los recursos naturales compartidos.",
  icon: Leaf
}, {
  title: "Ética y Transparencia",
  description: "Mantenemos los más altos estándares de integridad en cada alianza, asegurando que el impacto sea real y medible.",
  icon: ShieldCheck
}];
const testimonials = [{
  name: "Ricardo Méndez",
  role: "Director de Fundación Local",
  content: "La alianza con Kalisch Acero ha permitido rehabilitar tres centros comunitarios este año. Su compromiso con la comunidad es genuino y constante.",
  avatar: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1679911/img/impact_ricardo_m.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1679911/img/impact_ricardo_m.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1679911/img/impact_ricardo_m.png-wvc-sizes",
    alt: "Ricardo Méndez"
  }
}, {
  name: "Elena Vázquez",
  role: "Beneficiaria de Beca Técnica",
  content: "Gracias al programa de capacitación, hoy cuento con las herramientas para trabajar en la industria. Es una oportunidad que cambió mi vida.",
  avatar: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1679911/img/impact_elena_v.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1679911/img/impact_elena_v.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1679911/img/impact_elena_v.png-wvc-sizes",
    alt: "Elena Vázquez"
  }
}, {
  name: "Javier Soto",
  role: "Líder de Voluntariado",
  content: "Ver el impacto directo de nuestro trabajo en las escuelas locales nos motiva a seguir construyendo un mejor futuro para todos.",
  avatar: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1679911/img/impact_javier_s.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1679911/img/impact_javier_s.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1679911/img/impact_javier_s.png-wvc-sizes",
    alt: "Javier Soto"
  }
}];
export default function SocialImpact() {
  const {
    ref: headerRef,
    isVisible: isHeaderVisible
  } = useRevealOnScroll<HTMLDivElement>();
  const {
    ref: contentRef,
    isVisible: isContentVisible
  } = useRevealOnScroll<HTMLDivElement>();
  const {
    ref: testimonialsRef,
    isVisible: isTestimonialsVisible
  } = useRevealOnScroll<HTMLDivElement>();
  const {
    ref: ctaRef,
    isVisible: isCtaVisible
  } = useRevealOnScroll<HTMLDivElement>();
  return <section data-section-id="262648" id="social-impact" className="bg-background text-foreground py-20 md:py-32 overflow-hidden">
      <div className="container mx-auto px-4 md:px-6 max-w-7xl">
        
        {}
        <div ref={headerRef} className={`text-center max-w-3xl mx-auto mb-16 md:mb-24 transition-all duration-1000 ${isHeaderVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <Badge variant="secondary" className="mb-4 bg-primary/10 text-primary border-primary/20">
            Impacto Social
          </Badge>
          <h2 className="font-default text-4xl md:text-5xl font-bold text-foreground mb-6">
            Construyendo Valor Humano
          </h2>
          <p className="font-default text-lg md:text-xl text-muted-foreground leading-relaxed">
            En Kalisch Acero, nuestra fortaleza no solo reside en el acero, sino en el impacto positivo que generamos en las personas y sus comunidades.
          </p>
        </div>

        {}
        <div ref={contentRef} className={`grid lg:grid-cols-2 gap-12 lg:gap-20 items-center mb-24 transition-all duration-1000 delay-200 ${isContentVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <div className="order-2 lg:order-1 space-y-8">
            {features.map((feature, i) => <div key={i} data-index={i} className="flex gap-4 md:gap-6">
                <div className="flex-shrink-0 mt-1">
                  <div className="w-12 h-12 rounded-lg bg-accent/10 flex items-center justify-center border border-accent/20">
                    <feature.icon className="w-6 h-6 text-accent" />
                  </div>
                </div>
                <div>
                  <h3 className="font-default text-xl font-bold text-foreground mb-2">
                    {feature.title}
                  </h3>
                  <p className="font-default text-muted-foreground leading-relaxed">
                    {feature.description}
                  </p>
                </div>
              </div>)}
          </div>

          <div className="order-1 lg:order-2 relative">
            <div className="absolute inset-0 bg-primary/5 rounded-lg transform translate-x-4 translate-y-4 -z-10"></div>
            <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1679911/img/kalisch_social_impact_community.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1679911/img/kalisch_social_impact_community.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1679911/img/kalisch_social_impact_community.png-wvc-sizes" alt="Colaboradores de Kalisch Acero en actividad de voluntariado" className="rounded-lg shadow-lg w-full h-auto object-cover aspect-[4/5] md:aspect-square" loading="lazy" />
          </div>
        </div>

        {}
        <div ref={testimonialsRef} className={`mb-24 transition-all duration-1000 delay-300 ${isTestimonialsVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <div className="text-center mb-12">
            <h3 className="font-default text-3xl font-bold text-foreground mb-4">
              Historias de Éxito
            </h3>
            <p className="font-default text-muted-foreground max-w-2xl mx-auto">
              Conoce los testimonios de quienes forman parte de nuestra red de impacto y transformación social.
            </p>
          </div>

          <Carousel opts={{
          align: "start",
          loop: true
        }} className="w-full">
            <CarouselContent className="-ml-4 md:-ml-6">
              {testimonials.map((testimonial, i) => <CarouselItem key={i} data-index={i} className="pl-4 md:pl-6 md:basis-1/2 lg:basis-1/3">
                  <Card className="h-full bg-card border-border shadow-md hover:shadow-lg transition-shadow">
                    <CardHeader className="pb-4">
                      <Quote className="w-8 h-8 text-accent/30 mb-4" />
                      <CardDescription className="font-default text-base text-foreground leading-relaxed italic">
                        "{testimonial.content}"
                      </CardDescription>
                    </CardHeader>
                    <CardContent className="pt-0 mt-auto">
                      <div className="flex items-center gap-4 pt-4 border-t border-border">
                        <SafeHydrate><Avatar className="border-2 border-primary/10">
                          <AvatarImage src={testimonial.avatar.src} alt={testimonial.avatar.alt} data-wvc-srcset={testimonial.avatar.srcset} data-wvc-sizes={testimonial.avatar.sizes} />
                          <AvatarFallback className="bg-primary/5 text-primary font-default">
                            {testimonial.name.charAt(0)}
                          </AvatarFallback>
                        </Avatar></SafeHydrate>
                        <div>
                          <p className="font-default font-bold text-foreground text-sm">
                            {testimonial.name}
                          </p>
                          <p className="font-default text-xs text-muted-foreground">
                            {testimonial.role}
                          </p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </CarouselItem>)}
            </CarouselContent>
            <div className="flex justify-center gap-4 mt-8">
              <CarouselPrevious className="static translate-y-0 translate-x-0 bg-background border-border hover:bg-primary/10 hover:text-primary" />
              <CarouselNext className="static translate-y-0 translate-x-0 bg-background border-border hover:bg-primary/10 hover:text-primary" />
            </div>
          </Carousel>
        </div>

        {}
        <div ref={ctaRef} className={`bg-primary rounded-lg p-8 md:p-12 text-center shadow-xl border border-primary-600 relative overflow-hidden transition-all duration-1000 delay-400 ${isCtaVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <div className="absolute top-0 right-0 -mt-16 -mr-16 w-64 h-64 bg-accent/20 rounded-lg blur-3xl pointer-events-none"></div>
          <div className="absolute bottom-0 left-0 -mb-16 -ml-16 w-64 h-64 bg-secondary/10 rounded-lg blur-3xl pointer-events-none"></div>
          
          <div className="relative z-10 max-w-2xl mx-auto">
            <h3 className="font-default text-3xl md:text-4xl font-bold text-primary-foreground mb-4">
              ¿Quieres Sumarte a Nuestra Causa?
            </h3>
            <p className="font-default text-lg text-primary-foreground/90 mb-8">
              Buscamos aliados y organizaciones que compartan nuestra visión de un México más fuerte y solidario. Contáctanos para colaborar.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button asChild size="lg" className="bg-accent text-accent-foreground hover:bg-accent/90 font-default text-base rounded-md px-8">
                <Link to="/SustainabilityPage">
                  Ver Sostenibilidad
                  <ArrowRight className="ml-2 w-4 h-4" />
                </Link>
              </Button>
              <Button asChild variant="outline" size="lg" className="border-primary-foreground text-primary-foreground hover:bg-primary-foreground/10 font-default text-base rounded-md px-8">
                <Link to="/NosotrosPage">
                  Sobre Nosotros
                </Link>
              </Button>
            </div>
          </div>
        </div>

      </div>
    </section>;
}